<template>
  <app-page labelledby="reconciliation-create-title" full-width>
    <page-header
      title-id="reconciliation-create-title"
      title="Новый акт сверки взаиморасчетов"
      description="Период и контрагент определяют операции, обороты и сальдо сохраненного акта"
    >
      <template #actions>
        <q-btn flat no-caps label="К реестру" :disable="creating" :to="{ name: 'analytics_reconciliation' }" />
        <q-btn v-if="savedDocumentId" color="primary" no-caps icon="open_in_new" label="Открыть сохраненный акт" @click="openSavedAct" />
      </template>
    </page-header>

    <q-form ref="formNode" class="reconciliation-create" @submit.prevent="requestAct">
      <page-section title="Параметры сверки" description="Система сформирует акт по данным учета и сохранит его в реестре.">
        <div class="reconciliation-create__grid">
          <q-input v-model="periodStart" outlined :mask="dateInputMask" label="Период с" :rules="[requiredRule('Укажите начало периода'), validStartRule]">
            <template #append>
              <q-icon name="event" class="cursor-pointer" role="button" tabindex="0" aria-label="Выбрать начало периода" @keydown.enter.prevent="$event.currentTarget.click()" @keydown.space.prevent="$event.currentTarget.click()">
                <q-popup-proxy cover transition-show="scale" transition-hide="scale"><q-date v-model="periodStart" mask="DD.MM.YYYY"><div class="row justify-end"><q-btn v-close-popup flat no-caps color="primary" label="Готово" /></div></q-date></q-popup-proxy>
              </q-icon>
            </template>
          </q-input>
          <q-input v-model="periodEnd" outlined :mask="dateInputMask" label="Период по" :rules="[requiredRule('Укажите окончание периода'), validEndRule]">
            <template #append>
              <q-icon name="event" class="cursor-pointer" role="button" tabindex="0" aria-label="Выбрать окончание периода" @keydown.enter.prevent="$event.currentTarget.click()" @keydown.space.prevent="$event.currentTarget.click()">
                <q-popup-proxy cover transition-show="scale" transition-hide="scale"><q-date v-model="periodEnd" mask="DD.MM.YYYY"><div class="row justify-end"><q-btn v-close-popup flat no-caps color="primary" label="Готово" /></div></q-date></q-popup-proxy>
              </q-icon>
            </template>
          </q-input>
          <q-select
            v-model="selectedCounterparty"
            outlined use-input clearable input-debounce="350"
            :options="counterpartyOptions"
            :loading="counterpartiesLoading"
            label="Контрагент"
            no-option-label="Контрагенты не найдены"
            :rules="[requiredRule('Выберите контрагента')]"
            @filter="filterCounterparties"
          >
            <template #option="scope">
              <q-item v-bind="scope.itemProps">
                <q-item-section><q-item-label>{{ scope.opt.label }}</q-item-label><q-item-label caption>{{ scope.opt.caption }}</q-item-label></q-item-section>
              </q-item>
            </template>
          </q-select>
        </div>
        <div class="reconciliation-create__quick" aria-label="Быстрый выбор периода">
          <q-btn outline no-caps color="primary" label="Месяц" @click="setQuickPeriod('month')" />
          <q-btn outline no-caps color="primary" label="Квартал" @click="setQuickPeriod('quarter')" />
          <q-btn outline no-caps color="primary" label="Год" @click="setQuickPeriod('year')" />
          <q-btn flat no-caps color="primary" label="Сбросить" @click="resetForm" />
        </div>
      </page-section>

      <page-section title="Подписант нашей организации" description="Укажите уполномоченного подписанта. Эти данные сохранятся в акте и будут использованы в PDF и XML ФНС.">
        <div class="reconciliation-create__signer">
          <q-input v-model="signerName" outlined label="ФИО подписанта нашей организации" :rules="[requiredRule('Укажите ФИО подписанта'), signerNameRule]" />
          <q-input v-model="signerPosition" outlined label="Должность подписанта нашей организации" />
        </div>
        <p class="reconciliation-create__hint">Перед выгрузкой XML проверьте ИНН, КПП и ОГРН обеих сторон в сохраненном акте. Недостающие реквизиты можно заполнить в его карточке.</p>
      </page-section>

      <p v-if="errorMessage" class="reconciliation-create__error" role="alert">{{ errorMessage }}</p>
      <page-actions v-if="!savedDocumentId" aria-label="Формирование акта сверки">
        <q-btn flat no-caps label="Отмена" :to="{ name: 'analytics_reconciliation' }" />
        <q-btn color="primary" no-caps icon="auto_awesome" label="Сформировать и сохранить" type="submit" :loading="creating" :disable="creating || offline" />
      </page-actions>
    </q-form>

    <template v-if="savedDocumentId">
      <page-section title="Предпросмотр сформированного акта" description="Акт уже сохранен. Проверьте операции и итоговые показатели перед открытием карточки.">
        <div class="reconciliation-create__summary">
          <div><span>Контрагент</span><strong>{{ previewCounterparty }}</strong></div>
          <div><span>Период</span><strong>{{ previewPeriod }}</strong></div>
          <div><span>Оборот, дебет</span><amount-cell :value="preview.turnovers && preview.turnovers.my_debet" /></div>
          <div><span>Оборот, кредит</span><amount-cell :value="preview.turnovers && preview.turnovers.my_credit" /></div>
          <div><span>Конечное сальдо, дебет</span><amount-cell :value="preview.total && preview.total.my_total_debet" /></div>
          <div><span>Конечное сальдо, кредит</span><amount-cell :value="preview.total && preview.total.my_total_credit" /></div>
        </div>
        <app-data-table
          title="Операции сверки"
          :rows="previewRows"
          :columns="columns"
          :count="previewRows.length"
          :state="previewRows.length ? 'ready' : 'empty'"
          state-title="Операций за период нет"
          state-message="Акт сохранен с нулевыми оборотами."
          storage-key="act-sver-create-preview"
          data-testid="act-sver-preview"
        />
      </page-section>
      <page-actions aria-label="Переход к акту сверки">
        <q-btn outline no-caps label="К реестру" :to="{ name: 'analytics_reconciliation' }" />
        <q-btn color="primary" no-caps icon="open_in_new" label="Открыть сохраненный акт" @click="openSavedAct" />
      </page-actions>
    </template>
  </app-page>

  <confirm-dialog
    :model-value="leaveDialogOpen"
    title="Выйти без формирования акта?"
    body="Выбранные параметры сверки будут потеряны."
    confirm-label="Выйти"
    cancel-label="Остаться"
    destructive
    @cancel="cancelDirtyLeave"
    @confirm="confirmDirtyLeave"
    @update:model-value="handleLeaveDialogModel"
  />
</template>

<script>
import { formatMoney } from '../../services/moneyFormat'
import moment from 'moment'
import AmountCell from '../../components/ui/AmountCell.vue'
import AppDataTable from '../../components/ui/AppDataTable.vue'
import AppPage from '../../components/ui/AppPage.vue'
import ConfirmDialog from '../../components/ui/ConfirmDialog.vue'
import PageActions from '../../components/ui/PageActions.vue'
import PageHeader from '../../components/ui/PageHeader.vue'
import PageSection from '../../components/ui/PageSection.vue'
import {
  DATE_DISPLAY_FORMAT,
  DATE_INPUT_MASK,
  normalizePeriodRange,
  parseDate,
  unixEndOfDay,
  unixStartOfDay
} from '../../services/dateFormat'
import { requiredRule, validateAndFocus } from '../../services/formValidation'

const columns = Object.freeze([
  { name: 'my_date', label: 'Дата', align: 'left', field: 'my_date' },
  { name: 'my_name', label: 'Документ по нашим данным', align: 'left', field: 'my_name', required: true },
  { name: 'my_debet', label: 'Дебет', align: 'right', field: 'my_debet', format: (value) => formatMoney(value) },
  { name: 'my_credit', label: 'Кредит', align: 'right', field: 'my_credit', format: (value) => formatMoney(value) },
  { name: 'contr_name', label: 'Документ контрагента', align: 'left', field: 'contr_name' },
  { name: 'contr_debet', label: 'Дебет контрагента', align: 'right', field: 'contr_debet', format: (value) => formatMoney(value) },
  { name: 'contr_credit', label: 'Кредит контрагента', align: 'right', field: 'contr_credit', format: (value) => formatMoney(value) }
])

function currentQuarterRange() {
  return {
    from: moment().startOf('quarter').format(DATE_DISPLAY_FORMAT),
    to: moment().endOf('quarter').format(DATE_DISPLAY_FORMAT)
  }
}

export default {
  name: 'act_check_master',
  components: { AmountCell, AppDataTable, AppPage, ConfirmDialog, PageActions, PageHeader, PageSection },
  beforeRouteLeave(to, from, next) {
    if (this.isDirty) this.requestDirtyLeave(next)
    else next()
  },
  data() {
    const range = normalizePeriodRange(this.$store.state.filter_period || currentQuarterRange())
    return {
      columns,
      dateInputMask: DATE_INPUT_MASK,
      periodStart: range.from || currentQuarterRange().from,
      periodEnd: range.to || currentQuarterRange().to,
      selectedCounterparty: null,
      counterpartyOptions: [],
      counterpartiesLoading: false,
      creating: false,
      errorMessage: '',
      myOrgName: '',
      signerName: '',
      signerPosition: '',
      counterpartiesRequestId: null,
      createRequestId: null,
      savedDocumentId: null,
      preview: {},
      baseline: '',
      handlers: [],
      leaveDialogOpen: false,
      pendingNavigation: null
    }
  },
  computed: {
    offline() { return this.$store.state.connection_status === 'offline' },
    periodStartMoment() { return parseDate(this.periodStart) },
    periodEndMoment() { return parseDate(this.periodEnd) },
    isDirty() { return !this.savedDocumentId && Boolean(this.baseline) && this.snapshot() !== this.baseline },
    previewRows() { return Array.isArray(this.preview.rows) ? this.preview.rows : [] },
    previewCounterparty() { return this.preview.counterparty?.name || this.selectedCounterparty?.label || '—' },
    previewPeriod() {
      const period = this.preview.period || {}
      return period.start_display && period.end_display ? `${period.start_display} — ${period.end_display}` : `${this.periodStart} — ${this.periodEnd}`
    }
  },
  mounted() {
    moment.locale('ru')
    this.listen('onGetCounterparties', this.onCounterparties)
    this.listen('onCreateActSver', this.onCreateActSver)
    this.listen('didReceoveUserInfoAcrossOrg', this.onUserInfo)
    this.listen('connectionReady', this.loadInitial)
    this.markClean()
    if (this.connection.isAuthenticated) this.loadInitial()
  },
  beforeUnmount() { this.handlers.forEach(([event, handler]) => this.emitter.off(event, handler)) },
  methods: {
    requiredRule,
    signerNameRule(value) { return String(value || '').trim().split(/\s+/).length >= 2 || 'Укажите фамилию и имя подписанта' },
    listen(event, handler) { this.emitter.on(event, handler); this.handlers.push([event, handler]) },
    snapshot() { return JSON.stringify({ from: this.periodStart, to: this.periodEnd, counterparty: this.selectedCounterparty?.value || null, signerName: this.signerName, signerPosition: this.signerPosition }) },
    markClean() { this.baseline = this.snapshot() },
    validStartRule() { return this.periodStartMoment.isValid() || 'Введите дату в формате ДД.ММ.ГГГГ' },
    validEndRule() {
      if (!this.periodEndMoment.isValid()) return 'Введите дату в формате ДД.ММ.ГГГГ'
      return !this.periodStartMoment.isAfter(this.periodEndMoment) || 'Дата начала позже даты окончания'
    },
    loadInitial() {
      this.loadCounterparties()
      this.connection.getUserInfoAcrossOrg(this.connection.makeRequestId('user-org'))
    },
    optionFromCounterparty(item) {
      const name = item.name || item.short_name || item.full_name || 'Контрагент'
      return {
        label: name,
        caption: `${item.inn ? `ИНН ${item.inn}` : 'ИНН не указан'}${item.kpp ? ` / КПП ${item.kpp}` : ''}${item.address ? `, ${item.address}` : ''}`,
        value: item.id,
        raw: item
      }
    },
    loadCounterparties(search = '') {
      if (!this.connection.isAuthenticated) return
      this.counterpartiesLoading = true
      this.counterpartiesRequestId = this.connection.makeRequestId('counterparties')
      this.connection.getCounterparties(this.counterpartiesRequestId, { search, page: 1, page_size: 20, is_active: true })
    },
    filterCounterparties(value, update) { update(() => this.loadCounterparties(value)) },
    onCounterparties(response) {
      if (response.request_id !== this.counterpartiesRequestId) return
      this.counterpartiesLoading = false
      if (response.type === 'error' || response.error) {
        this.counterpartyOptions = []
        this.errorMessage = response.message || 'Не удалось загрузить контрагентов'
        return
      }
      this.counterpartyOptions = (((response.data || {}).items) || []).map(this.optionFromCounterparty)
      this.errorMessage = ''
    },
    onUserInfo(response) {
      const organisations = (((response || {}).user || {}).organisations || [])
      const current = organisations.find((item) => String(item.id) === String(this.connection.selectedOrgId(this.$q)))
      this.myOrgName = current ? current.name : ''
    },
    async requestAct() {
      this.errorMessage = ''
      if (this.creating || !(await validateAndFocus(this.$refs.formNode, this.$refs.formNode))) return
      this.creating = true
      this.createRequestId = this.connection.makeRequestId('act-sver')
      this.connection.createActSver(this.createRequestId, {
        period_start: unixStartOfDay(this.periodStart),
        period_end: unixEndOfDay(this.periodEnd),
        counterparty_id: this.selectedCounterparty.value,
        additional_data: {
          my_org_name: this.myOrgName,
          contr_org_name: this.selectedCounterparty.label,
          my_fio_name: this.signerName.trim(),
          my_position: this.signerPosition.trim()
        }
      })
    },
    onCreateActSver(response) {
      if (response.request_id !== this.createRequestId) return
      this.creating = false
      if (response.type === 'error' || response.error) {
        this.errorMessage = response.message || response.error || 'Не удалось сформировать акт сверки'
        return
      }
      this.savedDocumentId = response.document_id || response.data?.document_id || null
      this.preview = response.body || response.data?.body || response.data || {}
      this.markClean()
    },
    openSavedAct() {
      if (this.savedDocumentId) this.$router.push({ name: 'document_info_act_sver', params: { id: this.savedDocumentId, history_id: 'last' } })
    },
    setQuickPeriod(kind) {
      const base = moment()
      const unit = kind === 'year' ? 'year' : (kind === 'month' ? 'month' : 'quarter')
      this.periodStart = base.clone().startOf(unit).format(DATE_DISPLAY_FORMAT)
      this.periodEnd = base.clone().endOf(unit).format(DATE_DISPLAY_FORMAT)
    },
    resetForm() {
      const range = currentQuarterRange()
      this.periodStart = range.from
      this.periodEnd = range.to
      this.selectedCounterparty = null
      this.signerName = ''
      this.signerPosition = ''
      this.errorMessage = ''
      this.loadCounterparties()
    },
    requestDirtyLeave(next) { this.pendingNavigation = next; this.leaveDialogOpen = true },
    cancelDirtyLeave() { const next = this.pendingNavigation; this.pendingNavigation = null; this.leaveDialogOpen = false; if (next) next(false) },
    confirmDirtyLeave() { const next = this.pendingNavigation; this.pendingNavigation = null; this.baseline = ''; this.leaveDialogOpen = false; if (next) next() },
    handleLeaveDialogModel(value) { if (!value && this.leaveDialogOpen) this.cancelDirtyLeave() }
  }
}
</script>

<style scoped>
.reconciliation-create { display: grid; gap: var(--sid-space-6); }
.reconciliation-create__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)) minmax(18rem, 2fr); gap: var(--sid-space-3); }
.reconciliation-create__signer { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--sid-space-3); }
.reconciliation-create__hint { color: var(--sid-color-text-secondary); margin-bottom: 0; }
.reconciliation-create__quick { display: flex; flex-wrap: wrap; gap: var(--sid-space-2); margin-top: var(--sid-space-4); }
.reconciliation-create__summary { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--sid-space-4); margin-bottom: var(--sid-space-5); }
.reconciliation-create__summary div { display: grid; gap: var(--sid-space-1); min-width: 0; }
.reconciliation-create__summary span { color: var(--sid-color-text-secondary); font-size: var(--sid-font-size-label); }
.reconciliation-create__summary strong { overflow-wrap: anywhere; }
.reconciliation-create__error { margin: 0; color: var(--sid-color-danger); }
@media (max-width: 899px) { .reconciliation-create__grid, .reconciliation-create__summary { grid-template-columns: 1fr 1fr; } }
@media (max-width: 599px) { .reconciliation-create__grid, .reconciliation-create__summary, .reconciliation-create__signer { grid-template-columns: 1fr; } }
</style>
